// Course view (admin): opened from the 👁 icon on Courses Management. Shows everything a course holds
// (details, publish checklist, students, and every module → chapter → content item) in the Courses
// Management layout, previews any item in place, and — with "Edit Structure" — edits, deletes and
// reorders modules, chapters and items (and adds items to a chapter) on the same page, using the same API
// calls as the Course Structure Builder (/admin/courses/:id/structure, still used read-only by coaches).
// Modules and chapters aren't added here; that's done through Add Content.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronDown,
  Eye,
  FileQuestion,
  Layers,
  ListTree,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { api } from '../../api';
import { useRBAC } from '../../contexts/RBACContext';
import CourseCreationModal from '../../components/admin/CourseCreationModal';
import LoadingState from '../../components/common/LoadingState';
import { confirmDialog, escapeHtml, resultDialog } from '../../utils/swalDialogs';
import AssignCourseModal from './AssignCourseModal';
import { LIGHT_COURSE_LIST } from './courseThumbnails';
import CourseItemPreview from './CourseItemPreview';
import { ContentItemForm, TitleForm } from './CourseStructureForms';
import {
  CategoryBadge,
  CategorySidebar,
  DIFFICULTY_TONE,
  addContentPath,
  courseCounts,
  courseSubcategoryChain,
  isLastRow,
  promptAddCategory,
  useCourseCategories,
  useCourseSubcategories,
} from './coursesShared';
import { ITEM_TYPES, byOrder, canPreview, capitalise, coins, itemDetails, plural, quizOf } from './courseViewShared';

const STATUS_TONE = { published: 'green', draft: 'amber', archived: 'gray' };
const METADATA_CHECKS = new Set(['title', 'description', 'category', 'difficulty', 'thumbnail']);
const swalView = { customClass: { container: 'ds-swal', popup: 'ds-swal__popup', confirmButton: 'ds-swal__confirm' }, buttonsStyling: false, confirmButtonText: 'Close' };

const isImageUrl = (v) => typeof v === 'string' && /^(https?:|data:|blob:)/.test(v);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const legacyType = (fileType = '') => ['pdf', 'audio', 'video', 'image'].find((t) => fileType.includes(t)) || 'file';
const apiError = (err) => err?.response?.data?.error || err?.response?.data?.message || 'Please try again.';

// The chapter's content items, then the older chapter fields some early courses still use (video link,
// attached files, upload link, inline questions). Those older rows can be previewed but not edited here.
function chapterItems(ch) {
  const legacy = [];
  if (ch.videoUrl) legacy.push({ _id: `${ch._id}-video`, legacy: true, type: 'video', title: ch.videoTitle || 'Chapter video', fileUrl: ch.videoUrl });
  (ch.files || []).forEach((f, i) => legacy.push({ _id: `${ch._id}-file-${i}`, legacy: true, type: legacyType(f.fileType), title: f.fileName || 'File', fileUrl: f.fileUrl }));
  if (ch.uploadLink) legacy.push({ _id: `${ch._id}-upload`, legacy: true, type: 'link', title: 'Upload link', externalUrl: ch.uploadLink });
  if (ch.quizzes?.length) legacy.push({ _id: `${ch._id}-questions`, legacy: true, type: 'quiz', title: 'Chapter questions', quizData: { questions: ch.quizzes } });
  return [...byOrder(ch.contentItems), ...legacy];
}

const chapterCoins = (ch) =>
  coins(ch.defaultCoins)
  + (ch.contentItems || []).reduce((n, item) => n + coins(item.coins), 0)
  + (ch.coinRules || []).reduce((n, rule) => n + coins(rule.coins), 0);

// Chapter thumbnails are stored as private S3 links, so they are shown through a signed view link.
function ChapterThumb({ src }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <span className="cv-chapter__thumb">
      {src && !failed ? <img src={src} alt="" onError={() => setFailed(true)} /> : <span aria-hidden="true">📖</span>}
    </span>
  );
}

function ToolButton({ label, onClick, disabled, danger = false, children }) {
  return (
    <button type="button" className={`cv-tool${danger ? ' cv-tool--danger' : ''}`} aria-label={label} title={label} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

// Up / down / edit / delete controls shown next to a module, chapter or item while editing.
function MoveEditDelete({ name, index, count, busy, onMove, onEdit, onDelete }) {
  return (
    <div className="cv-tools">
      <ToolButton label={`Move ${name} up`} disabled={busy || index === 0} onClick={() => onMove(-1)}><ArrowUp size={15} /></ToolButton>
      <ToolButton label={`Move ${name} down`} disabled={busy || index === count - 1} onClick={() => onMove(1)}><ArrowDown size={15} /></ToolButton>
      <ToolButton label={`Edit ${name}`} disabled={busy} onClick={onEdit}><Pencil size={15} /></ToolButton>
      <ToolButton label={`Delete ${name}`} disabled={busy} danger onClick={onDelete}><Trash2 size={15} /></ToolButton>
    </div>
  );
}

/**
 * Course view (admin). SECURITY: requires 'LMS Management' 'Manage', like the rest of Courses Management.
 */
export default function CourseView() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, isLoading: rbacLoading, permissions } = useRBAC();
  const { categories, reload: reloadCategories } = useCourseCategories();
  const { nodesFor } = useCourseSubcategories();

  const [course, setCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({});
  const [checks, setChecks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [thumbs, setThumbs] = useState({});
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [sidebarCategory, setSidebarCategory] = useState('');
  const [editing, setEditing] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [editMode, setEditMode] = useState(() => new URLSearchParams(location.search).get('edit') === '1');
  const [preview, setPreview] = useState(null);
  const [dialog, setDialog] = useState(null); // { kind: 'module' | 'chapter' | 'item', mode: 'add' | 'edit', module?, chapter?, item? }
  const moreRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (rbacLoading || !Object.keys(permissions).length) return;
    if (!hasPermission('LMS Management', 'Manage')) navigate('/access-denied');
  }, [hasPermission, navigate, rbacLoading, permissions]);

  // `quiet` refreshes in place (after publish / edit / assign / structure changes) without the loading screen.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const [courseRes, listRes, summaryRes, checksRes] = await Promise.all([
        api.get(`/api/v2/lms/admin/courses/${courseId}`),
        // Without thumbnails (~17 MB with them); this course's own comes with it above.
        api.get('/api/v2/lms/admin/courses', { params: LIGHT_COURSE_LIST }).catch(() => null),
        api.get('/api/v2/lms/admin/courses/summary').catch(() => null),
        api.get(`/api/v2/lms/admin/courses/${courseId}/validate`).catch(() => null),
      ]);
      const data = courseRes.data?.data;
      if (!data) throw new Error('Course not found');
      setCourse(data);
      if (listRes) setCourses(listRes.data?.data || []);
      setStats(summaryRes?.data?.data?.courses?.[courseId] || {});
      setChecks(checksRes?.data?.success ? checksRes.data : null);
    } catch (err) {
      setCourse(null);
      setError(err?.response?.status === 404 ? 'This course no longer exists.' : 'Failed to load the course. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  const canManage = !rbacLoading && hasPermission('LMS Management', 'Manage');
  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  useEffect(() => setCollapsed(new Set()), [courseId]);
  useEffect(() => { if (course?.category) setSidebarCategory(course.category); }, [course?.category]);
  // The row the course is in (English › Action Songs › Vocal Songs): the sidebar opens the way down
  // to it and marks it and the rows above it. A course right in its category marks just the category.
  const sidebarSubcategory = useMemo(
    () => (course?.category ? courseSubcategoryChain(nodesFor(course.category), course).pop()?.key || '' : ''),
    [course, nodesFor]
  );

  useEffect(() => {
    if (!moreOpen) return undefined;
    const close = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [moreOpen]);

  const modules = useMemo(
    () => byOrder(course?.modules).map((m) => ({ ...m, chapters: byOrder(m.chapters).map((ch) => ({ ...ch, items: chapterItems(ch) })) })),
    [course]
  );

  useEffect(() => {
    const urls = [...new Set(modules.flatMap((m) => m.chapters.map((ch) => ch.thumbnail)).filter((u) => /^https?:\/\//i.test(u || '')))];
    if (!urls.length) return undefined;
    let alive = true;
    Promise.all(urls.map((url) => api.get('/api/v2/lms/admin/courses/files/view-url', { params: { url } })
      .then((r) => [url, r.data?.data?.url || url])
      .catch(() => [url, url])))
      .then((pairs) => { if (alive) setThumbs(Object.fromEntries(pairs)); });
    return () => { alive = false; };
  }, [modules]);

  const goAddContent = (categoryName, id) => {
    const params = new URLSearchParams({ category: categoryName });
    if (id) params.set('course', id);
    // The form's Back button comes back to this course page.
    navigate(`/admin/courses/content/new?${params.toString()}`, { state: { from: `${location.pathname}${location.search}` } });
  };
  const addContent = () => (course.category ? goAddContent(course.category, course._id) : startEditing());
  // A category or subcategory picked in the sidebar opens its page in Courses Management.
  const openInCourses = (categoryName, sub) => {
    const params = new URLSearchParams();
    if (categoryName) params.set('category', categoryName);
    if (categoryName && sub) params.set('sub', sub);
    const qs = params.toString();
    navigate(`/admin/courses${qs ? `?${qs}` : ''}`);
  };

  const startEditing = () => {
    setEditMode(true);
    setCollapsed(new Set());
    contentRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const publish = async () => {
    setBusy('publish');
    try {
      await api.put(`/api/v2/lms/admin/courses/${courseId}/publish`);
      resultDialog('success', 'Course published', 'It can now be assigned to students.');
    } catch (err) {
      const errors = err?.response?.data?.errors;
      if (errors?.length) {
        Swal.fire({ ...swalView, icon: 'error', title: 'Cannot publish yet', html: `<ul class="cv-swal-list">${errors.map((e) => `<li>${escapeHtml(e)}</li>`).join('')}</ul>` });
      } else {
        resultDialog('error', 'Could not publish the course', apiError(err));
      }
    } finally {
      setBusy('');
      load({ quiet: true });
    }
  };

  // ---- Structure editing (same endpoints as the Course Structure Builder) ----
  const base = `/api/v2/lms/admin/courses/${courseId}`;

  // Runs one change; on success shows `doneTitle`, refreshes the page and resolves true.
  const change = async (request, doneTitle, failTitle) => {
    try {
      await request();
      if (doneTitle) resultDialog('success', doneTitle);
      await load({ quiet: true });
      return true;
    } catch (err) {
      resultDialog('error', failTitle, apiError(err));
      return false;
    }
  };
  const closeDialogAfter = async (promise) => {
    const ok = await promise;
    if (ok) setDialog(null);
    return ok;
  };

  const saveModule = (data) => closeDialogAfter(dialog.mode === 'add'
    ? change(() => api.post(`${base}/modules`, data), 'Module added', 'Could not add the module')
    : change(() => api.put(`${base}/modules/${dialog.module._id}`, data), 'Module updated', 'Could not update the module'));

  const saveChapter = (data) => closeDialogAfter(dialog.mode === 'add'
    ? change(() => api.post(`${base}/modules/${dialog.module._id}/chapters`, data), 'Chapter added', 'Could not add the chapter')
    : change(() => api.put(`${base}/modules/${dialog.module._id}/chapters/${dialog.chapter._id}`, data), 'Chapter updated', 'Could not update the chapter'));

  const saveItem = (data) => {
    const chapterUrl = `${base}/modules/${dialog.module._id}/chapters/${dialog.chapter._id}/content`;
    return closeDialogAfter(dialog.mode === 'add'
      ? change(() => api.post(chapterUrl, data), 'Content item added', 'Could not add the content item')
      : change(() => api.put(`${chapterUrl}/${dialog.item._id}`, data), 'Content item updated', 'Could not update the content item'));
  };

  const remove = async ({ title, html, url, doneTitle, failTitle }) => {
    if (!(await confirmDialog({ title, html, confirmText: 'Delete', cancelText: 'Keep it', danger: true }))) return;
    setBusy('remove');
    await change(() => api.delete(url), doneTitle, failTitle);
    setBusy('');
  };
  const deleteModule = (m) => remove({
    title: 'Delete this module?',
    html: `<b>${escapeHtml(m.title)}</b> and all its chapters and content items will be deleted.`,
    url: `${base}/modules/${m._id}`,
    doneTitle: 'Module deleted',
    failTitle: 'Could not delete the module',
  });
  const deleteChapter = (m, ch) => remove({
    title: 'Delete this chapter?',
    html: `<b>${escapeHtml(ch.title)}</b> and all its content items will be deleted.`,
    url: `${base}/modules/${m._id}/chapters/${ch._id}`,
    doneTitle: 'Chapter deleted',
    failTitle: 'Could not delete the chapter',
  });
  const deleteItem = (m, ch, item) => remove({
    title: 'Delete this content item?',
    html: `<b>${escapeHtml(item.title)}</b> will be removed from this chapter.`,
    url: `${base}/modules/${m._id}/chapters/${ch._id}/content/${item._id}`,
    doneTitle: 'Content item deleted',
    failTitle: 'Could not delete the content item',
  });

  // Swaps one entry with its neighbour and saves the new order (PUT /reorder, as the builder's drag and drop does).
  const move = async (level, list, index, dir, parentId) => {
    const next = [...list];
    [next[index], next[index + dir]] = [next[index + dir], next[index]];
    setBusy('reorder');
    await change(() => api.put(`${base}/reorder`, { level, ...(parentId ? { parentId } : {}), orderedIds: next.map((x) => x._id) }), '', 'Could not change the order');
    setBusy('');
  };

  // Quiz items are edited in the quiz editor, as in the builder; everything else in the item form.
  const editItem = (m, ch, item) => {
    const quizId = quizOf(item)?._id;
    if (item.type === 'quiz' && quizId) navigate(`/admin/quizzes/${quizId}/edit`);
    else setDialog({ kind: 'item', mode: 'edit', module: m, chapter: ch, item });
  };

  const toggleModule = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allCollapsed = modules.length > 0 && modules.every((m) => collapsed.has(m._id));

  if (rbacLoading) return <LoadingState message="Loading permissions..." fullScreen />;

  const counts = courseCounts(course);
  const totalCoins = modules.reduce((n, m) => n + m.chapters.reduce((k, ch) => k + chapterCoins(ch), 0), 0);
  const emptyChapters = modules.reduce((n, m) => n + m.chapters.filter((ch) => !(ch.contentItems || []).length).length, 0);
  // The list leaves thumbnails out; the Edit form shows and keeps this course's, so it gets it.
  const listCourse = course && { ...(courses.find((c) => c._id === courseId) || course), thumbnail: course.thumbnail };
  const failingChecks = (checks?.checks || []).filter((c) => c.status === 'fail');
  const working = Boolean(busy);

  return (
    <div className="cm-page">
      <div className="cm-layout">
        <CategorySidebar
          categories={categories}
          selected={sidebarCategory}
          onSelect={(name) => openInCourses(name || sidebarCategory)}
          onAddCategory={async () => { if (await promptAddCategory(categories)) reloadCategories(); }}
          subcategoryNodes={nodesFor}
          selectedSubcategory={sidebarSubcategory}
          onSelectSubcategory={(name, node) => (isLastRow(node)
            // A last subcategory opens Add New Content for it, like in Courses Management; Back returns here.
            ? navigate(addContentPath(name, node.key), { state: { from: `${location.pathname}${location.search}` } })
            : openInCourses(name, node.key))}
        />

        <main className="cm-main">
          <div className="ca-crumbs">
            <button type="button" className="cm-btn cm-btn--ghost cm-btn--sm" onClick={() => navigate('/admin/courses')}><ArrowLeft size={16} />Back</button>
            <span className="cv-crumbs__root">Courses Management</span>
            {course && <><span className="cv-crumbs__sep" aria-hidden="true">›</span><b className="cv-crumbs__title">{course.title}</b></>}
          </div>

          {loading ? (
            <section className="cm-card"><LoadingState message="Loading course..." /></section>
          ) : error || !course ? (
            <section className="cm-card cv-error">
              <AlertCircle size={28} />
              <p>{error || 'Course not found.'}</p>
              <div>
                <button type="button" className="cm-btn cm-btn--ghost cm-btn--sm" onClick={() => navigate('/admin/courses')}>Back to Courses</button>
                <button type="button" className="cm-btn cm-btn--purple cm-btn--sm" onClick={() => load()}><RefreshCw size={15} />Try again</button>
              </div>
            </section>
          ) : (
            <>
              <section className="cv-hero" aria-label="Course details">
                <span className="cv-hero__thumb">
                  {isImageUrl(course.thumbnail) ? <img src={course.thumbnail} alt="" /> : <span aria-hidden="true">{course.icon || '📚'}</span>}
                </span>
                <div className="cv-hero__body">
                  <div className="cv-hero__title">
                    <h1>{course.title}</h1>
                    <span className={`cm-pill cm-pill--${STATUS_TONE[course.status] || 'gray'}`}>{capitalise(course.status)}</span>
                  </div>
                  <p>{course.description || 'No description yet.'}</p>
                  <div className="cv-hero__meta">
                    <CategoryBadge categories={categories} name={course.category} />
                    <span className={`cm-pill cm-pill--${DIFFICULTY_TONE[course.difficultyLevel] || 'gray'}`}>{course.difficultyLevel || '—'}</span>
                    {course.duration && <span>⏱ {course.duration}</span>}
                    {course.status === 'published' && course.publishedAt && <span>Published {fmtDate(course.publishedAt)}</span>}
                    {course.updatedAt && <span>Updated {fmtDate(course.updatedAt)}</span>}
                    {course.createdBy?.name && <span>Created by {course.createdBy.name}</span>}
                  </div>
                </div>
                <div className="cv-hero__actions">
                  {course.status === 'draft' && (
                    <button type="button" className="cm-btn cv-btn--green" onClick={publish} disabled={busy === 'publish'}>
                      <Upload size={17} />{busy === 'publish' ? 'Publishing…' : 'Publish Course'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="cm-btn cm-btn--blue"
                    onClick={() => setAssignOpen(true)}
                    disabled={course.status !== 'published'}
                    title={course.status === 'published' ? 'Assign to students' : 'Publish the course before assigning it'}
                  >
                    <UserPlus size={17} />Assign Course
                  </button>
                  <button type="button" className="cm-btn cm-btn--purple" onClick={addContent}><Plus size={17} />Add Content</button>
                  <button type="button" className="cm-btn cm-btn--soft" onClick={() => setEditing(true)}><Pencil size={16} />Edit Details</button>
                  {editMode ? (
                    <button type="button" className="cm-btn cv-btn--done" onClick={() => setEditMode(false)} aria-pressed="true"><Check size={17} />Done Editing</button>
                  ) : (
                    <button type="button" className="cm-btn cm-btn--soft" onClick={startEditing} aria-pressed="false"><ListTree size={16} />Edit Structure</button>
                  )}
                  <div className="cm-more" ref={moreRef}>
                    <button type="button" className="cm-btn cm-btn--icon" aria-label="More course actions" onClick={() => setMoreOpen((o) => !o)}><MoreHorizontal size={18} /></button>
                    {moreOpen && (
                      <div className="cm-menu" role="menu">
                        <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); navigate(`/admin/courses/${courseId}/quizzes/create`); }}><FileQuestion size={16} />Manage Quizzes</button>
                        <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); load({ quiet: true }); }}><RefreshCw size={16} />Refresh</button>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section className="cm-stats" aria-label="Course numbers">
                <div className="cm-stat cm-stat--blue">
                  <span className="cm-stat__icon"><Layers size={26} /></span>
                  <div><b>{counts.modules}</b><span>Modules</span><small>Sections in this course</small></div>
                </div>
                <div className="cm-stat cm-stat--green">
                  <span className="cm-stat__icon"><BookOpen size={26} /></span>
                  <div><b>{counts.chapters}</b><span>Chapters</span><small>{emptyChapters ? `${emptyChapters} without content` : counts.chapters ? 'All have content' : 'None yet'}</small></div>
                </div>
                <div className="cm-stat cm-stat--amber">
                  <span className="cm-stat__icon"><ListTree size={26} /></span>
                  <div><b>{counts.items}</b><span>Content Items</span><small>{totalCoins} ISF coins in total</small></div>
                </div>
                <div className="cm-stat cm-stat--pink">
                  <span className="cm-stat__icon"><Users size={26} /></span>
                  <div><b>{stats.studentsAssigned || 0}</b><span>Students Assigned</span><small>{stats.averageProgress || 0}% average progress</small></div>
                </div>
              </section>

              {course.status === 'draft' && checks && (
                <section className="cm-card" aria-label="Publish checklist">
                  <div className="cm-card__head">
                    <h2>{checks.canPublish ? 'Ready to publish' : 'Before you publish'}</h2>
                    <small className="cv-head-note">
                      {checks.canPublish ? 'Everything required is in place. Click Publish Course when you are ready.' : 'Fix the items marked in red, then click Publish Course.'}
                    </small>
                  </div>
                  <ul className="cv-checks">
                    {checks.checks.map((c) => (
                      <li key={c.id} className={`cv-check is-${c.status}`}>
                        {c.status === 'pass' ? <CheckCircle2 size={18} /> : c.status === 'fail' ? <XCircle size={18} /> : <AlertCircle size={18} />}
                        <div>
                          <b>{c.label}</b>
                          <small>{c.message}</small>
                          {c.status === 'fail' && METADATA_CHECKS.has(c.id) && (
                            <button type="button" className="cm-link" onClick={() => setEditing(true)}>Edit details</button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {!checks.canPublish && !failingChecks.length && checks.errors?.length > 0 && (
                    <ul className="cv-errors">
                      {checks.errors.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  )}
                </section>
              )}

              <section className={`cm-card${editMode ? ' cv-editing' : ''}`} aria-label="Course content" ref={contentRef}>
                <div className="cm-card__head">
                  <h2>Course Content</h2>
                  <div className="cv-head-actions">
                    <small className="cv-head-note">{plural(counts.modules, 'module')} · {plural(counts.chapters, 'chapter')} · {plural(counts.items, 'item')}</small>
                    {modules.length > 0 && (
                      <button type="button" className="cm-btn cm-btn--ghost cm-btn--sm" onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(modules.map((m) => m._id)))}>
                        {allCollapsed ? 'Expand all' : 'Collapse all'}
                      </button>
                    )}
                  </div>
                </div>

                {editMode && (
                  <p className="cv-editbar">
                    <Pencil size={15} />
                    <span>Editing structure. Edit, delete or move modules, chapters and items, or add items to a chapter; every change is saved straight away.</span>
                    <button type="button" className="cm-link" onClick={() => setEditMode(false)}>Done editing</button>
                  </p>
                )}

                {!modules.length ? (
                  <div className="cv-empty">
                    <p>This course has no modules yet.</p>
                    <small>Use <b>Add Content</b> to add a story or lesson.</small>
                  </div>
                ) : (
                  <div className="cv-modules">
                    {modules.map((m, mi) => {
                      const open = !collapsed.has(m._id);
                      const itemTotal = m.chapters.reduce((n, ch) => n + ch.items.length, 0);
                      return (
                        <section key={m._id} className="cv-module">
                          <div className="cv-module__head">
                            <button type="button" className="cv-module__toggle" aria-expanded={open} onClick={() => toggleModule(m._id)}>
                              <span className="cv-module__num">{mi + 1}</span>
                              <span className="cv-module__title">
                                <b>{m.title}</b>
                                {m.description && <small>{m.description}</small>}
                              </span>
                              <span className="cv-module__meta">{plural(m.chapters.length, 'chapter')} · {plural(itemTotal, 'item')}</span>
                              <ChevronDown size={18} className={`cv-module__chev${open ? ' is-open' : ''}`} />
                            </button>
                            {editMode && (
                              <MoveEditDelete
                                name={`module ${m.title}`}
                                index={mi}
                                count={modules.length}
                                busy={working}
                                onMove={(dir) => move('module', modules, mi, dir)}
                                onEdit={() => setDialog({ kind: 'module', mode: 'edit', module: m })}
                                onDelete={() => deleteModule(m)}
                              />
                            )}
                          </div>

                          {open && (
                            <div className="cv-module__body">
                              {!m.chapters.length && (
                                <p className="cv-note cv-note--warn">No chapters in this module yet. Add one before publishing.</p>
                              )}
                              {m.chapters.map((ch, ci) => {
                                const editableItems = ch.items.filter((item) => !item.legacy);
                                return (
                                  <article key={ch._id || ci} className="cv-chapter">
                                    <header className="cv-chapter__head">
                                      <ChapterThumb src={thumbs[ch.thumbnail]} />
                                      <div className="cv-chapter__title">
                                        <small>Chapter {mi + 1}.{ci + 1}</small>
                                        <b>{ch.title}</b>
                                        {ch.description && <p>{ch.description}</p>}
                                      </div>
                                      <span className="cv-coins" title="Default coins + item coins + coin rules"><i>₹</i>{chapterCoins(ch)} coins</span>
                                      {editMode && (
                                        <MoveEditDelete
                                          name={`chapter ${ch.title}`}
                                          index={ci}
                                          count={m.chapters.length}
                                          busy={working}
                                          onMove={(dir) => move('chapter', m.chapters, ci, dir, m._id)}
                                          onEdit={() => setDialog({ kind: 'chapter', mode: 'edit', module: m, chapter: ch })}
                                          onDelete={() => deleteChapter(m, ch)}
                                        />
                                      )}
                                    </header>

                                    {(coins(ch.defaultCoins) > 0 || ch.coinRules?.length > 0) && (
                                      <div className="cv-rules">
                                        <span>Default coins: <b>{coins(ch.defaultCoins)}</b></span>
                                        {(ch.coinRules || []).map((r, ri) => <span key={`${r.label}-${ri}`}>{r.label}: <b>{coins(r.coins)}</b></span>)}
                                      </div>
                                    )}

                                    {!ch.items.length ? (
                                      <p className="cv-note cv-note--warn">No content in this chapter yet. Every chapter needs at least one item before the course can be published.</p>
                                    ) : (
                                      <div className="cm-table-wrap">
                                        <table className={`cm-table cv-items${editMode ? ' cv-items--edit' : ''}`}>
                                          <thead>
                                            <tr>
                                              <th>Sr. No.</th>
                                              <th>Content Type</th>
                                              <th>Title / Details</th>
                                              <th className="is-num">ISF Coins</th>
                                              <th>Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {ch.items.map((item, ii) => {
                                              const type = ITEM_TYPES[item.type] || ITEM_TYPES.file;
                                              const details = itemDetails(item);
                                              const editIndex = editableItems.indexOf(item);
                                              return (
                                                <tr key={item._id || ii}>
                                                  <td>{ii + 1}</td>
                                                  <td><span className="ca-type"><i aria-hidden="true">{type.icon}</i>{type.label}</span></td>
                                                  <td>
                                                    <div className="cv-item">
                                                      <b>{item.title}</b>
                                                      {details && <small>{details}</small>}
                                                    </div>
                                                  </td>
                                                  <td className="is-num">{item.coins != null ? <span className="cv-coin-chip"><i>₹</i>{coins(item.coins)}</span> : '—'}</td>
                                                  <td>
                                                    <div className="cv-item__actions">
                                                      <button type="button" className="ca-act ca-act--view" onClick={() => setPreview(item)} disabled={!canPreview(item)} aria-label={`View ${item.title}`}>
                                                        <Eye size={15} />View
                                                      </button>
                                                      {editMode && !item.legacy && (
                                                        <MoveEditDelete
                                                          name={item.title}
                                                          index={editIndex}
                                                          count={editableItems.length}
                                                          busy={working}
                                                          onMove={(dir) => move('content_item', editableItems, editIndex, dir, ch._id)}
                                                          onEdit={() => editItem(m, ch, item)}
                                                          onDelete={() => deleteItem(m, ch, item)}
                                                        />
                                                      )}
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}

                                    {editMode && (
                                      <div className="cv-chapter__foot">
                                        <button type="button" className="cv-add" onClick={() => setDialog({ kind: 'item', mode: 'add', module: m, chapter: ch })}>
                                          <Plus size={15} />Add item to {ch.title}
                                        </button>
                                      </div>
                                    )}
                                  </article>
                                );
                              })}
                            </div>
                          )}
                        </section>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </main>
      </div>

      {preview && <CourseItemPreview item={preview} onClose={() => setPreview(null)} />}

      {dialog?.kind === 'module' && (
        <TitleForm
          heading={dialog.mode === 'add' ? 'Add New Module' : 'Edit Module'}
          noun="Module"
          initial={dialog.module}
          placeholder="e.g., Introduction to MS Word"
          submitLabel={dialog.mode === 'add' ? 'Add Module' : 'Save Changes'}
          onSubmit={saveModule}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'chapter' && (
        <TitleForm
          heading={dialog.mode === 'add' ? `Add Chapter to ${dialog.module.title}` : 'Edit Chapter'}
          noun="Chapter"
          initial={dialog.chapter}
          placeholder="e.g., Document Basics"
          submitLabel={dialog.mode === 'add' ? 'Add Chapter' : 'Save Changes'}
          onSubmit={saveChapter}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'item' && (
        <ContentItemForm
          mode={dialog.mode}
          item={dialog.item}
          courseId={courseId}
          chapterTitle={dialog.chapter.title}
          onSubmit={saveItem}
          onClose={() => setDialog(null)}
        />
      )}

      {editing && course && (
        <CourseCreationModal
          isOpen
          courseToEdit={listCourse}
          categories={categories.map((c) => c.name)}
          subcategoryNodes={nodesFor}
          onClose={() => setEditing(false)}
          onCourseCreated={() => {
            setEditing(false);
            resultDialog('success', 'Course updated');
            load({ quiet: true });
            reloadCategories();
          }}
        />
      )}

      {assignOpen && course && (
        <AssignCourseModal
          categories={categories}
          courses={courses}
          initialCourse={listCourse}
          onClose={() => setAssignOpen(false)}
          onAssigned={() => { setAssignOpen(false); load({ quiet: true }); }}
        />
      )}
    </div>
  );
}
